import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Avatar,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getUpcomingEvents,
  getEventGroups,
  createEvent,
} from '../../../endpoints/eventsTs';
import {
  CreateEventRequest,
  UpcomingEventResponse,
  HostRequest,
} from '../../../types/events';
import { eventType, groupTypes } from '../../../utils/variables';
import AlertItem from '../../../Components/AlertItem';
import UserSearchModal from '../../../Components/UserSearchModal';
import StateSelector from '../../../Components/StateSelector';
import CountrySelector from '../../../Components/CountrySelector';
import { getCountries, getStates } from '../../../endpoints/lookups';

// Import icon images
import BookGroupIcon from '../../../assets/BookGroupIcon.png';
import WineGroupIcon from '../../../assets/WineGroupIcon.png';
import WalkingRunningGroupIcon from '../../../assets/WalkingRunningGroupIcon.png';
import DinnerGroupIcon from '../../../assets/DinnerGroupIcon.png';
import CoffeeGroupIcon from '../../../assets/CoffeeGroupIcon.png';
import CraftsGroupIcon from '../../../assets/CraftsGroupIcon.png';
import GameGroupIcon from '../../../assets/GameGroupIcon.png';
import SocialMeetingIconGold from '../../../assets/SocialMeetingIconGold.png';
import LocalChapterMeetingIcon from '../../../assets/LocalChapterMeetingIcon.png';
import DefaultIcon from '../../../assets/DefaultIcon.png';

dayjs.extend(utc);

const QUERY_START = 'start';
const QUERY_END = 'end';

function defaultEventsDateRange(): { start: Dayjs; end: Dayjs } {
  return {
    start: dayjs().startOf('day'),
    end: dayjs().add(90, 'day').startOf('day'),
  };
}

/** Parse list filter range from URL; invalid or partial values fall back to defaults. */
function parseEventsRangeSearchParams(sp: URLSearchParams): {
  start: Dayjs;
  end: Dayjs;
} {
  const defs = defaultEventsDateRange();
  const rawS = sp.get(QUERY_START);
  const rawE = sp.get(QUERY_END);
  if (!rawS && !rawE) return defs;

  const parseOne = (raw: string | null) => {
    if (!raw) return null;
    const d = dayjs(raw, 'YYYY-MM-DD', true);
    return d.isValid() ? d.startOf('day') : null;
  };

  let s = parseOne(rawS);
  let e = parseOne(rawE);
  if (rawS && !s) return defs;
  if (rawE && !e) return defs;
  if (!s && !e) return defs;
  if (s && !e) e = s.add(90, 'day');
  if (!s && e) s = e.subtract(90, 'day');
  if (!s || !e) return defs;
  if (s.isAfter(e)) {
    const t = s;
    s = e;
    e = t;
  }
  return { start: s, end: e };
}

function applyRangeToSearchParams(
  prev: URLSearchParams,
  start: Dayjs,
  end: Dayjs
): URLSearchParams {
  const next = new URLSearchParams(prev);
  next.set(QUERY_START, start.format('YYYY-MM-DD'));
  next.set(QUERY_END, end.format('YYYY-MM-DD'));
  return next;
}

interface LCEventsProps {
  id: string;
  lcAccess: boolean;
  canEditEvents: boolean;
}

const LCEvents: React.FC<LCEventsProps> = ({ id, lcAccess, canEditEvents }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rangeSeededRef = useRef(false);
  const lastSubmittedMeetingOnRef = useRef<string | null>(null);

  const [startDate, setStartDate] = useState<Dayjs>(
    () => defaultEventsDateRange().start
  );
  const [endDate, setEndDate] = useState<Dayjs>(
    () => defaultEventsDateRange().end
  );

  // Seed default range into the URL when no params (shareable address bar); sync state on URL changes (back/forward).
  useEffect(() => {
    if (!rangeSeededRef.current) {
      rangeSeededRef.current = true;
      if (!searchParams.get(QUERY_START) && !searchParams.get(QUERY_END)) {
        const d = defaultEventsDateRange();
        setSearchParams(
          applyRangeToSearchParams(searchParams, d.start, d.end),
          {
            replace: true,
          }
        );
        return;
      }
    }
    const parsed = parseEventsRangeSearchParams(searchParams);
    setStartDate(parsed.start);
    setEndDate(parsed.end);
  }, [searchParams, setSearchParams]);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createData, setCreateData] = useState<Partial<CreateEventRequest>>({
    title: '',
    description: '',
    meeting_on: '',
    ending_on: '',
    meeting_link: '',
    event_type_id: '',
    local_chapter_id: id,
    group_id: '',
    location_name: '',
    location_user_id: '',
    meeting_address: {},
    hosts: [],
  });

  // Location type: 'member' or 'business'
  const [createLocationType, setCreateLocationType] = useState<
    'member' | 'business'
  >('member');

  // Selected country/state objects for selectors
  const [createSelectedCountry, setCreateSelectedCountry] = useState<any>(null);

  // User search modals
  const [showLocationUserModal, setShowLocationUserModal] = useState(false);
  const [showHostUserModal, setShowHostUserModal] = useState(false);

  // Store host names for display (host ID -> name)
  const [createHostNames, setCreateHostNames] = useState<Map<string, string>>(
    new Map()
  );

  // Fetch countries and states for lookup (selectors use their own fetch; keep for cache/consistency)
  useApiGet(['donation-countries'], getCountries, {
    enabled: true,
  });

  const countryIdForStates =
    createSelectedCountry?.id || createData.meeting_address?.country_id;
  useApiGet(
    ['donation-states', countryIdForStates],
    () => getStates({ countryId: countryIdForStates }),
    {
      enabled: !!countryIdForStates,
    }
  );

  // Format dates for API call
  const startDateStr = useMemo(
    () => startDate.format('YYYY-MM-DD'),
    [startDate]
  );
  const endDateStr = useMemo(() => endDate.format('YYYY-MM-DD'), [endDate]);

  // Get timezone offset in hours (JavaScript returns minutes, inverted sign)
  // Convert to standard format: UTC-5 (EST) = -5, UTC+5 = +5
  const timezoneOffset = useMemo(() => {
    return -new Date().getTimezoneOffset() / 60;
  }, []);

  // Fetch events
  const {
    data: eventsData,
    isFetching,
    refetch,
  } = useApiGet(
    ['lc-events', id, startDateStr, endDateStr, timezoneOffset],
    () =>
      getUpcomingEvents(
        startDateStr,
        endDateStr,
        id,
        undefined,
        timezoneOffset
      ),
    {
      enabled: !!id,
      retry: 1,
    }
  );

  // Fetch event groups for this chapter (not only the user's current chapter)
  const { data: eventGroups } = useApiGet(
    ['event-groups', id],
    () => getEventGroups(id),
    {
      enabled: !!id,
      retry: 1,
    }
  );

  // Events are already filtered by the backend based on start_date and end_date
  const filteredEvents = eventsData || [];

  const handleStartDateChange = (newValue: Dayjs | null) => {
    if (!newValue) return;
    const nextStart = newValue.startOf('day');
    let nextEnd = endDate;
    if (nextStart.isAfter(nextEnd)) {
      nextEnd = nextStart;
    }
    setStartDate(nextStart);
    setEndDate(nextEnd);
    setSearchParams(
      applyRangeToSearchParams(searchParams, nextStart, nextEnd),
      {
        replace: true,
      }
    );
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    if (!newValue) return;
    const nextEnd = newValue.startOf('day');
    let nextStart = startDate;
    if (nextEnd.isBefore(nextStart)) {
      nextStart = nextEnd;
    }
    setStartDate(nextStart);
    setEndDate(nextEnd);
    setSearchParams(
      applyRangeToSearchParams(searchParams, nextStart, nextEnd),
      {
        replace: true,
      }
    );
  };

  const handleEventClick = (eventId: string) => {
    navigate(`/local-chapters/${id}/events/${eventId}`);
  };

  const handleCreateEvent = () => {
    setShowCreateModal(true);
    setCreateLocationType('member');
    setCreateData({
      title: '',
      description: '',
      meeting_on: '',
      ending_on: '',
      meeting_link: '',
      event_type_id: '',
      local_chapter_id: id,
      group_id: '',
      location_name: '',
      location_user_id: '',
      meeting_address: {},
      hosts: [],
    });
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateError(null);
    setCreateLocationType('member');
    setCreateSelectedCountry(null);
    setCreateHostNames(new Map());
    setCreateData({
      title: '',
      description: '',
      meeting_on: '',
      ending_on: '',
      meeting_link: '',
      event_type_id: '',
      local_chapter_id: id,
      group_id: '',
      location_name: '',
      location_user_id: '',
      meeting_address: {},
      hosts: [],
    });
  };

  const { mutate: createEventMutation } = useApiSend(
    (data: CreateEventRequest) => createEvent(data),
    () => {
      setIsSaving(false);
      setShowCreateModal(false);
      const meetingOn = lastSubmittedMeetingOnRef.current;
      lastSubmittedMeetingOnRef.current = null;
      if (meetingOn) {
        const meetingDay = dayjs(meetingOn).startOf('day');
        setSearchParams(
          (prev) => {
            const parsed = parseEventsRangeSearchParams(prev);
            const newEnd = meetingDay.isAfter(parsed.end.startOf('day'))
              ? meetingDay
              : parsed.end;
            let newStart = parsed.start;
            if (newEnd.isBefore(newStart)) newStart = newEnd;
            return applyRangeToSearchParams(prev, newStart, newEnd);
          },
          { replace: true }
        );
      }
      refetch();
    },
    (error: Error) => {
      setIsSaving(false);
      lastSubmittedMeetingOnRef.current = null;
      console.error('Failed to create event:', error);
    }
  );

  // Convert local datetime to UTC ISO format for API
  const convertToUTCISO = (dateTimeString: string): string => {
    if (!dateTimeString) return dateTimeString;
    // Parse as local time and convert to UTC ISO
    return dayjs(dateTimeString).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
  };

  const handleSaveEvent = () => {
    // Clear previous errors
    setCreateError(null);

    // Validate required fields
    if (
      !createData.title ||
      !createData.meeting_on ||
      !createData.event_type_id
    ) {
      setCreateError('Please fill in all required fields.');
      return;
    }

    // Validate end date is required
    if (!createData.ending_on) {
      setCreateError('Ending date/time is required.');
      return;
    }

    // Validate end date is not before start date
    if (dayjs(createData.ending_on).isBefore(dayjs(createData.meeting_on))) {
      setCreateError(
        'Ending date/time cannot be before the meeting date/time.'
      );
      return;
    }

    setIsSaving(true);
    lastSubmittedMeetingOnRef.current = createData.meeting_on || null;
    const cleanedData = prepareEventData(createData, createLocationType);
    // Convert dates to UTC ISO format
    if (cleanedData.meeting_on) {
      cleanedData.meeting_on = convertToUTCISO(cleanedData.meeting_on);
    }
    if (cleanedData.ending_on) {
      cleanedData.ending_on = convertToUTCISO(cleanedData.ending_on);
    }
    createEventMutation(cleanedData as CreateEventRequest);
  };

  // Location type change handlers
  const handleCreateLocationTypeChange = (type: 'member' | 'business') => {
    setCreateLocationType(type);
    if (type === 'member') {
      setCreateData({
        ...createData,
        location_name: '',
        meeting_address: {},
      });
      setCreateSelectedCountry(null);
    } else {
      setCreateData({
        ...createData,
        location_user_id: '',
      });
    }
  };

  // Country/State change handlers for create form
  const handleCreateCountryChange = (country: any) => {
    setCreateSelectedCountry(country);
    setCreateData({
      ...createData,
      meeting_address: {
        ...createData.meeting_address,
        country_id: country?.id || '',
        state_id: '', // Reset state when country changes
      },
    });
  };

  const handleCreateStateChange = (state: any) => {
    setCreateData({
      ...createData,
      meeting_address: {
        ...createData.meeting_address,
        state_id: state?.id || '',
      },
    });
  };

  // User selection handlers
  const handleLocationUserSelected = (user: any) => {
    setCreateData({
      ...createData,
      location_user_id: user.id,
    });
    setShowLocationUserModal(false);
  };

  const handleHostUserSelected = (user: any) => {
    const isFirst = !createData.hosts || createData.hosts.length === 0;
    const newHost: HostRequest = {
      user_id: user.id,
      is_primary: isFirst,
    };

    const hostName = user.full_name || user.name || `User ${user.id}`;
    const updatedHosts = createData.hosts ? [...createData.hosts] : [];
    updatedHosts.push(newHost);
    setCreateData({
      ...createData,
      hosts: updatedHosts,
    });
    const newNames = new Map(createHostNames);
    newNames.set(user.id, hostName);
    setCreateHostNames(newNames);
    setShowHostUserModal(false);
  };

  const handleAddHost = () => {
    setShowHostUserModal(true);
  };

  const handleRemoveHost = (index: number) => {
    const updatedHosts = createData.hosts ? [...createData.hosts] : [];
    const removedHost = updatedHosts[index];
    updatedHosts.splice(index, 1);
    setCreateData({
      ...createData,
      hosts: updatedHosts,
    });
    if (removedHost) {
      const newNames = new Map(createHostNames);
      const idToRemove =
        (removedHost as HostRequest & { id?: string }).user_id ??
        (removedHost as HostRequest & { id?: string }).id;
      if (idToRemove) newNames.delete(idToRemove);
      setCreateHostNames(newNames);
    }
  };

  const handleSetPrimaryHost = (index: number) => {
    const updatedHosts = createData.hosts
      ? createData.hosts.map((host, i) => ({
          ...host,
          is_primary: i === index,
        }))
      : [];
    setCreateData({
      ...createData,
      hosts: updatedHosts,
    });
  };

  // Helper function to convert empty strings to null (except inside meeting_address,
  // where empty strings must be preserved so the API can clear optional address fields).
  const convertEmptyToNull = (value: any): any => {
    if (value === '') return null;
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const result: any = {};
      for (const key in value) {
        if (key === 'meeting_address') {
          result[key] = value[key];
          continue;
        }
        result[key] = convertEmptyToNull(value[key]);
      }
      return result;
    }
    if (Array.isArray(value)) {
      return value.map(convertEmptyToNull);
    }
    return value;
  };

  // Clean up data before saving based on location type
  const prepareEventData = (
    data: Partial<CreateEventRequest>,
    locationType: 'member' | 'business'
  ): Partial<CreateEventRequest> => {
    const cleaned: Partial<CreateEventRequest> = { ...data };

    if (locationType === 'member') {
      // Remove business location fields
      delete cleaned.location_name;
      delete cleaned.meeting_address;
      // Keep location_user_id if set, otherwise delete
      if (!cleaned.location_user_id) {
        delete cleaned.location_user_id;
      }
    } else {
      // Remove member location field
      delete cleaned.location_user_id;
      // Keep location_name and meeting_address if location_name is set
      if (!cleaned.location_name) {
        delete cleaned.location_name;
        delete cleaned.meeting_address;
      }
    }

    // Remove empty hosts array
    if (!cleaned.hosts || cleaned.hosts.length === 0) {
      delete cleaned.hosts;
    }

    // Convert empty strings to null
    return convertEmptyToNull(cleaned);
  };

  // Event type options for create form (Meeting and Social), matching edit form
  const eventTypes = useMemo(
    () => [
      { id: eventType.Meeting, name: 'Meeting' },
      { id: eventType.Social, name: 'Social' },
    ],
    []
  );

  const formatEventDate = (dateString: string): string => {
    if (!dateString) return '';
    // Parse UTC date and convert to local time for display
    return dayjs.utc(dateString).local().format('MMM D, YYYY h:mm A');
  };

  // Get event icon based on group type and event type (matching mobile app logic)
  const getEventIcon = (event: UpcomingEventResponse) => {
    if (event.group_type_id || event.event_type_id === eventType.Social) {
      switch (event.group_type_id) {
        case groupTypes.Book:
          return BookGroupIcon;
        case groupTypes.Wine:
          return WineGroupIcon;
        case groupTypes.Exercis: // Fitness
          return WalkingRunningGroupIcon;
        case groupTypes.Dining:
          return DinnerGroupIcon;
        case groupTypes.Coffee:
          return CoffeeGroupIcon;
        case groupTypes.Crafts:
          return CraftsGroupIcon;
        case groupTypes.Game:
          return GameGroupIcon;
        case groupTypes.Committee:
          if (event.event_type_id === eventType.Meeting) {
            return DefaultIcon;
          } else {
            return SocialMeetingIconGold;
          }
        default:
          return SocialMeetingIconGold;
      }
    } else {
      return LocalChapterMeetingIcon;
    }
  };

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid container spacing={2} sx={{ p: 1 }}>
        <Grid item xs={12} sm={4}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={handleStartDateChange}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={handleEndDateChange}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          {canEditEvents && (
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={handleCreateEvent}
              disabled={isSaving}
              fullWidth
              size="small"
              sx={{ height: '40px' }}
            >
              Create Event
            </Button>
          )}
        </Grid>
      </Grid>

      {!filteredEvents || filteredEvents.length === 0 ? (
        <Grid item xs={12} textAlign={'center'} paddingTop={2}>
          <Typography variant="body1">
            No events found in the selected date range
          </Typography>
        </Grid>
      ) : (
        filteredEvents.map((event) => (
          <Grid item xs={12} md={6} sx={{ p: 1 }} key={event.id}>
            <Card
              sx={{
                p: 2,
                boxShadow: 3,
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6,
                },
              }}
              onClick={() => handleEventClick(event.id)}
            >
              <CardHeader
                avatar={
                  <Avatar
                    src={getEventIcon(event)}
                    alt="Event icon"
                    sx={{ width: 40, height: 40 }}
                  />
                }
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">{event.title}</Typography>
                    {event.attending && (
                      <Chip label="Attending" color="success" size="small" />
                    )}
                  </Box>
                }
                subheader={
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {formatEventDate(event.meeting_on)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {event.event_type_name}
                      {event.group_name && ` • ${event.group_name}`}
                    </Typography>
                  </Box>
                }
              />
              {event.description && (
                <CardContent sx={{ p: 1, pt: 0 }}>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {event.description}
                  </Typography>
                </CardContent>
              )}
            </Card>
          </Grid>
        ))
      )}

      {/* Create Event Modal */}
      <Dialog
        open={showCreateModal}
        onClose={handleCloseCreateModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Event</DialogTitle>
        <DialogContent>
          {createError && (
            <Box sx={{ mb: 2, width: '100%' }}>
              <AlertItem
                severity="error"
                description={createError}
                onClose={() => setCreateError(null)}
                keepOpen={true}
              />
            </Box>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Event Title"
                value={createData.title}
                onChange={(e) =>
                  setCreateData({ ...createData, title: e.target.value })
                }
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={createData.description || ''}
                onChange={(e) =>
                  setCreateData({ ...createData, description: e.target.value })
                }
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Meeting Date/Time"
                value={
                  createData.meeting_on ? dayjs(createData.meeting_on) : null
                }
                onChange={(newValue) =>
                  setCreateData({
                    ...createData,
                    meeting_on: newValue
                      ? newValue.format('YYYY-MM-DD HH:mm:ss')
                      : '',
                  })
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    size: 'small',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Ending Date/Time"
                value={
                  createData.ending_on ? dayjs(createData.ending_on) : null
                }
                onChange={(newValue) => {
                  setCreateData({
                    ...createData,
                    ending_on: newValue
                      ? newValue.format('YYYY-MM-DD HH:mm:ss')
                      : '',
                  });
                  // Clear error when user changes the value
                  if (createError) setCreateError(null);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    size: 'small',
                    error:
                      createError !== null &&
                      (!createData.ending_on ||
                        dayjs(createData.ending_on).isBefore(
                          dayjs(createData.meeting_on)
                        )),
                    helperText:
                      createError &&
                      (!createData.ending_on ||
                        dayjs(createData.ending_on).isBefore(
                          dayjs(createData.meeting_on)
                        ))
                        ? createError
                        : '',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={createData.event_type_id || ''}
                  label="Event Type"
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      event_type_id: e.target.value,
                    })
                  }
                >
                  {eventTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Group (Optional)</InputLabel>
                <Select
                  value={createData.group_id || ''}
                  label="Group (Optional)"
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      group_id: e.target.value || '',
                    })
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {eventGroups?.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Meeting Link (Optional)"
                value={createData.meeting_link || ''}
                onChange={(e) =>
                  setCreateData({ ...createData, meeting_link: e.target.value })
                }
                fullWidth
              />
            </Grid>

            {/* Location Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <FormLabel component="legend">Location</FormLabel>
              <RadioGroup
                row
                value={createLocationType}
                onChange={(e) =>
                  handleCreateLocationTypeChange(
                    e.target.value as 'member' | 'business'
                  )
                }
              >
                <FormControlLabel
                  value="member"
                  control={<Radio />}
                  label="Member's House"
                />
                <FormControlLabel
                  value="business"
                  control={<Radio />}
                  label="Business Location"
                />
              </RadioGroup>
            </Grid>

            {createLocationType === 'member' && (
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  onClick={() => setShowLocationUserModal(true)}
                  fullWidth
                >
                  {createData.location_user_id
                    ? 'Change Location Member'
                    : 'Select Location Member'}
                </Button>
              </Grid>
            )}

            {createLocationType === 'business' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    label="Location Name"
                    value={createData.location_name || ''}
                    onChange={(e) =>
                      setCreateData({
                        ...createData,
                        location_name: e.target.value,
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address Line 1"
                    value={createData.meeting_address?.address_line_1 || ''}
                    onChange={(e) =>
                      setCreateData({
                        ...createData,
                        meeting_address: {
                          ...createData.meeting_address,
                          address_line_1: e.target.value,
                        },
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address Line 2 (Optional)"
                    value={createData.meeting_address?.address_line_2 || ''}
                    onChange={(e) =>
                      setCreateData({
                        ...createData,
                        meeting_address: {
                          ...createData.meeting_address,
                          address_line_2: e.target.value,
                        },
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="City"
                    value={createData.meeting_address?.city || ''}
                    onChange={(e) =>
                      setCreateData({
                        ...createData,
                        meeting_address: {
                          ...createData.meeting_address,
                          city: e.target.value,
                        },
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CountrySelector
                    value={createData.meeting_address?.country_id || ''}
                    handleChange={handleCreateCountryChange}
                    required={false}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StateSelector
                    value={createData.meeting_address?.state_id || ''}
                    handleChange={handleCreateStateChange}
                    countryId={createData.meeting_address?.country_id}
                    required={false}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Postal Code"
                    value={createData.meeting_address?.postal_code || ''}
                    onChange={(e) =>
                      setCreateData({
                        ...createData,
                        meeting_address: {
                          ...createData.meeting_address,
                          postal_code: e.target.value,
                        },
                      })
                    }
                    fullWidth
                  />
                </Grid>
              </>
            )}

            {/* Hosts Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <FormLabel component="legend">Hosts (Optional)</FormLabel>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddHost}
                >
                  Add Host
                </Button>
              </Box>
              {createData.hosts && createData.hosts.length > 0 && (
                <List dense>
                  {createData.hosts.map((host, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveHost(index)}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          createHostNames.get(host.user_id || host.id || '') ||
                          `Host ${index + 1}`
                        }
                        secondary={
                          <Box>
                            <Chip
                              label={
                                host.is_primary ? 'Primary' : 'Not Primary'
                              }
                              size="small"
                              color={host.is_primary ? 'primary' : 'default'}
                              onClick={() => handleSetPrimaryHost(index)}
                              sx={{ cursor: 'pointer', mr: 1 }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateModal} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEvent}
            variant="contained"
            color="success"
            disabled={
              isSaving ||
              !createData.title ||
              !createData.meeting_on ||
              !createData.ending_on ||
              !createData.event_type_id
            }
          >
            Create Event
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Search Modals */}
      <UserSearchModal
        modalOpen={showLocationUserModal}
        setUser={handleLocationUserSelected}
        setModalOpen={setShowLocationUserModal}
        chapter={id}
        spd={undefined}
        userObjectRet={true}
        statusFilter="active"
      />
      <UserSearchModal
        modalOpen={showHostUserModal}
        setUser={handleHostUserSelected}
        setModalOpen={setShowHostUserModal}
        chapter={id}
        spd={undefined}
        userObjectRet={true}
        statusFilter="active"
      />
    </Grid>
  );
};

export default LCEvents;
